/**
 * TrainingSessionAudioExportDialog
 *
 * Dialog for requesting server-side audio export of a training session.
 * Adapted from SimulationAudioExportDialog.js
 */

import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Alert, CircularProgress, Box,
  FormControlLabel, Checkbox
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import trainingApi from '../../services/trainingApi';

const TrainingSessionAudioExportDialog = ({
  open, onClose, sessionId, messageCount,
  studentVoiceId, patientVoiceId
}) => {
  const [includeSystemMessages, setIncludeSystemMessages] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await trainingApi.requestAudioExport(sessionId, {
        studentVoiceId,
        patientVoiceId,
        includeSystemMessages,
      });

      const data = response?.data || response;
      if (data.success) {
        setSuccess({
          jobId: data.jobId,
          message: data.message,
          estimatedTime: data.estimatedTime,
        });
        setTimeout(() => onClose(), 3000);
      }
    } catch (err) {
      console.error('Error requesting training audio export:', err);
      setError(err.message || 'Failed to request audio export. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const estimatedDuration = Math.ceil((messageCount * 3) / 60);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <DownloadIcon />
          <Typography variant="h6">Export Session Audio</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>{success.message}</Typography>
            <Typography variant="body2">Estimated time: {success.estimatedTime}</Typography>
            <Typography variant="caption" display="block" mt={1}>
              The audio file will be available for download once processing is complete.
            </Typography>
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                This will generate a downloadable MP3 audio file of the training session
                using the selected voices. The process runs in the background.
              </Typography>
            </Alert>

            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Cost Notice:</strong> Audio generation uses ElevenLabs TTS credits.
                Each message consumes credits based on text length.
              </Typography>
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Export details:
              </Typography>
              <Typography variant="body2">
                {messageCount} messages to process
              </Typography>
              <Typography variant="body2">
                Estimated audio duration: ~{estimatedDuration} minutes
              </Typography>
              <Typography variant="body2">
                Processing time: 15-20 minutes
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={includeSystemMessages}
                  onChange={(e) => setIncludeSystemMessages(e.target.checked)}
                  disabled={isSubmitting}
                />
              }
              label="Include system messages in export"
            />

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <QueueMusicIcon />}
          >
            {isSubmitting ? 'Requesting...' : 'Request Export'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TrainingSessionAudioExportDialog;
